// frontend/src/pages/ProductDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from '../routing';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { ArrowLeft, Shield, Clock, RotateCcw, Sparkles, Plus, Minus, Bell, Star, MapPin, Phone, ExternalLink, RefreshCw } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import { getIntlLocale } from '../i18n';
import { PLACEHOLDER_IMG } from '../config';
import { Reveal } from '../components/Motion';
import TraderIdentity from '../components/TraderIdentity';
import { useCompany } from '../utils/useCompany';
import theme from '../theme';

// The self-hosted copy in public/fonts, referenced once from index.html.
// This used to be a Google Fonts URL injected per page, which disclosed every
// visitor's IP to a third party on every page load and contradicted the privacy
// policy's "no third-party requests" claim. Kept as a constant because several
// pages import it into an @import rule rather than a <link>.
const FONTS = '/fonts/fonts.css';

// Postgres hands numeric columns back as strings, so an unguarded template
// string would happily build a maps URL out of "null,null" and drop the
// customer in the Gulf of Guinea.
const mapsUrl = (m) => {
  const lat = Number(m?.latitude);
  const lng = Number(m?.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) return null;
  return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
};

// The pilot cabinet stands on the street outside the salon, so it really is
// reachable at 3am — which is the single best thing about collecting from it,
// and worth saying in words rather than printing "24/7" as raw data. It is read
// OUT OF the record and not hardcoded, because a second cabinet may well sit
// indoors on shop hours, and the field is what an operator can correct.
const alwaysOpen = (m) => /24\s*[\/x-]\s*7|24ω|24 ώρες/i.test(m?.opening_hours || '');

// Money rates arrive from Postgres as "15.00". Printing that verbatim in a
// sentence reads like a form field; printing Number(x) turns 15.50 into 15.5.
// Whole numbers lose the decimals, the rest keep two — and a missing value falls
// back to the rate the schema defaults to, never to NaN in a price disclosure.
const fmtRate = (v, fallback) => {
  const n = Number(v);
  const rate = Number.isFinite(n) && n >= 0 ? n : fallback;
  return Number.isInteger(rate) ? String(rate) : rate.toFixed(2);
};

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  // For the GPSR Art. 16 "responsible person in the Union" block — that is us,
  // as importer, so it comes from the one runtime source of the trader identity.
  const company = useCompany();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(3);
  const [submitting, setSubmitting] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [reviews, setReviews]     = useState({ reviews: [], average: null, count: 0 });
  const [onWaitlist, setOnWaitlist] = useState(false);

  // Where the customer would actually be collecting from. The lockers table has
  // carried address, coordinates, phone and opening hours for a long time and
  // none of it ever reached a customer: this page showed a free-text field with
  // a hardcoded "Nicosia Mall" fallback, so nobody could find out where they
  // were going, or when it was reachable, until after they had paid.
  const [machines, setMachines]           = useState(null);
  const [machinesFailed, setMachinesFailed] = useState(false);
  const [machinesNonce, setMachinesNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setMachinesFailed(false);
    api.get('/locker/machines')
      .then(list => { if (!cancelled) setMachines(Array.isArray(list) ? list : []); })
      // Not silent, but not alarming either: the product's own locker_location
      // string still renders underneath, so the customer is never left with a
      // blank where the collection point should be.
      .catch(() => { if (!cancelled) { setMachines([]); setMachinesFailed(true); } });
    return () => { cancelled = true; };
  }, [machinesNonce]);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet'; link.href = FONTS;
    document.head.appendChild(link);
    api.get(`/products/${id}`)
      .then(p => {
        setProduct(p);
        api.get(`/reviews/product/${p.id}`).then(setReviews).catch(() => {});
        if (user) api.get(`/products/${p.id}/waitlist`).then(d => setOnWaitlist(d.on_waitlist)).catch(() => {});
      })
      .catch(() => navigate('/browse'))
      .finally(() => setLoading(false));
    return () => document.head.removeChild(link);
  }, [id]);

  const handleWaitlist = async () => {
    if (!user) { navigate('/login'); return; }
    try {
      await api.post(`/products/${id}/waitlist`, {});
      setOnWaitlist(true);
      toast.success(t('productDetail.waitlistToast'));
    } catch (err) {
      // Unhandled rejection before: the button did nothing at all — no state
      // change, no toast — and the customer had no way to tell it had failed.
      toast.error(err?.error || t('productDetail.waitlistError'));
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 120, fontFamily: theme.font.display, fontSize: 24, color: theme.gold }}>...</div>;
  if (!product) return null;

  const extraDays = Math.max(0, days - 3);
  const baseTotal = product.base_price_per_day * Math.min(days, 3);
  const extraTotal = extraDays * (product.extra_day_price || 5);
  const rentalTotal = baseTotal + extraTotal;
  const grandTotal = rentalTotal + parseFloat(product.deposit_amount);

  const handleRent = async () => {
    if (!user) { navigate('/login'); return; }
    if (product.status !== 'available') { toast.error(t('productDetail.notAvailableToast')); return; }
    setSubmitting(true);
    try {
      const data = await api.post('/rentals', { product_id: id, rental_days: days });
      toast.success(t('productDetail.rentalCreatedToast'));
      navigate(`/checkout/${data.rental.id}`);
    } catch (err) {
      toast.error(err.error || t('productDetail.rentalFailedToast'));
    } finally {
      setSubmitting(false);
    }
  };

  // The pilot runs one cabinet. Presenting that as a list of one reads like the
  // customer has a choice to make, when what they need is "here is where you go".
  const onePoint = (machines || []).length === 1;

  const images = product.image_urls?.length
    ? product.image_urls
    : [PLACEHOLDER_IMG];
  const safeIdx = Math.min(activeImg, images.length - 1);

  const statusLabel = product.status === 'available'
    ? t('productDetail.availableNow')
    : product.status === 'reserved'
    ? t('productDetail.beingBooked')
    // 'preparing' is the API's word for a set we hold but have not loaded
    // into a machine yet (037) — not rented, just not there.
    : product.status === 'preparing'
    ? t('productDetail.comingSoon')
    : t('productDetail.currentlyRented');

  return (
    <div style={{ minHeight: '100vh', background: theme.cream, fontFamily: theme.font.body }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '40px 24px' }}>

        <Link to="/browse" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: theme.grey400, textDecoration: 'none', fontSize: 13, marginBottom: 40, letterSpacing: '0.05em' }}>
          <ArrowLeft size={16} /> {t('productDetail.backToBrowse')}
        </Link>

        <div className="product-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 64, alignItems: 'start' }}>

          {/* Gallery */}
          <Reveal y={24}>
            <div style={{ position: 'relative', borderRadius: 4, overflow: 'hidden', aspectRatio: '3/4', background: theme.creamBone, marginBottom: 12 }}>
              <img
                key={safeIdx}
                src={images[safeIdx]}
                alt={product.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'opacity 0.25s' }}
              />
              {images.length > 1 && (
                <>
                  <button
                    onClick={() => setActiveImg(i => (i - 1 + images.length) % images.length)}
                    style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', width: 40, height: 40, borderRadius: '50%', background: theme.alpha.white(0.85), border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 2px 8px ${theme.alpha.black(0.15)}` }}>
                    <ArrowLeft size={18} />
                  </button>
                  <button
                    onClick={() => setActiveImg(i => (i + 1) % images.length)}
                    style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', width: 40, height: 40, borderRadius: '50%', background: theme.alpha.white(0.85), border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 2px 8px ${theme.alpha.black(0.15)}` }}>
                    <ArrowLeft size={18} style={{ transform: 'rotate(180deg)' }} />
                  </button>
                  <div style={{ position: 'absolute', bottom: 12, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 6 }}>
                    {images.map((_, i) => (
                      <button key={i} onClick={() => setActiveImg(i)} style={{ width: i === safeIdx ? 20 : 8, height: 8, borderRadius: 4, background: i === safeIdx ? theme.white : theme.alpha.white(0.55), border: 'none', cursor: 'pointer', padding: 0, transition: 'width 0.2s' }} />
                    ))}
                  </div>
                </>
              )}
            </div>
            {images.length > 1 && (
              <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
                {images.map((url, i) => (
                  <button key={i} onClick={() => setActiveImg(i)} style={{ flexShrink: 0, width: 72, height: 72, borderRadius: 4, overflow: 'hidden', border: i === safeIdx ? `2px solid ${theme.gold}` : '2px solid transparent', padding: 0, cursor: 'pointer', background: theme.creamBone, transition: 'border-color 0.15s' }}>
                    <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </button>
                ))}
              </div>
            )}
          </Reveal>

          {/* Info */}
          <Reveal delay={0.12} y={24} style={{ position: 'sticky', top: 100 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 16 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: product.status === 'available' ? theme.green : theme.red }} />
              <span style={{ fontSize: 12, color: product.status === 'available' ? theme.green : theme.red, letterSpacing: '0.1em' }}>
                {statusLabel}
              </span>
            </div>

            <h1 style={{ fontFamily: theme.font.display, fontSize: 'clamp(28px, 4vw, 48px)', fontWeight: 400, color: theme.ink, margin: '0 0 8px', lineHeight: 1.2 }}>
              {product.name}
            </h1>
            <p style={{ color: theme.grey400, fontSize: 14, marginBottom: 24 }}>{t(`catalog.color.${product.color_key}`, { defaultValue: product.color_key })}</p>

            {/* Specs */}
            <div style={{ display: 'flex', gap: 24, marginBottom: 32, paddingBottom: 32, borderBottom: `1px solid ${theme.sand}` }}>
              {[
                [t('productDetail.length'), `${product.length_cm}cm`],
                [t('productDetail.weight'), `${product.weight_grams}g`],
                [t('productDetail.texture'), product.texture_key
                  ? t(`catalog.texture.${product.texture_key}`, { defaultValue: product.texture_key })
                  : null],
              ].filter(([,v]) => v).map(([label, val]) => (
                <div key={label}>
                  <div style={{ color: theme.taupe, fontSize: 11, letterSpacing: '0.1em', marginBottom: 4 }}>{label.toUpperCase()}</div>
                  <div style={{ fontFamily: theme.font.display, fontSize: 18, color: theme.ink }}>{val}</div>
                </div>
              ))}
            </div>

            <p style={{ color: theme.grey600, fontSize: 15, lineHeight: 1.8, marginBottom: 32 }}>{product.description}</p>

            {/* Day selector */}
            <div style={{ background: theme.white, border: `1px solid ${theme.sand}`, borderRadius: 4, padding: 28, marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <div>
                  <div style={{ fontFamily: theme.font.display, fontSize: 20, color: theme.ink }}>{t('productDetail.rentalDuration')}</div>
                  <div style={{ color: theme.grey400, fontSize: 12, marginTop: 2 }}>{t('productDetail.minDays', { price: product.extra_day_price || 5 })}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <button onClick={() => setDays(Math.max(3, days - 1))} style={{ width: 36, height: 36, border: `1px solid ${theme.sand}`, borderRadius: '50%', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Minus size={16} />
                  </button>
                  <span style={{ fontFamily: theme.font.display, fontSize: 28, color: theme.ink, minWidth: 32, textAlign: 'center' }}>{days}</span>
                  <button onClick={() => setDays(Math.min(30, days + 1))} style={{ width: 36, height: 36, border: `1px solid ${theme.sand}`, borderRadius: '50%', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Plus size={16} />
                  </button>
                </div>
              </div>

              {/* Price breakdown */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: theme.grey600 }}>
                  <span>{t('productDetail.baseRental', { days: Math.min(days, 3), price: product.base_price_per_day })}</span>
                  <span>€{(product.base_price_per_day * Math.min(days, 3)).toFixed(2)}</span>
                </div>
                {extraDays > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: theme.grey600 }}>
                    <span>{t('productDetail.extraDays', { days: extraDays, price: product.extra_day_price || 5 })}</span>
                    <span>€{extraTotal.toFixed(2)}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', color: theme.grey600 }}>
                  <span>{t('productDetail.refundableDeposit')}</span>
                  <span>€{product.deposit_amount}</span>
                </div>
                <div style={{ borderTop: `1px solid ${theme.sand}`, paddingTop: 12, display: 'flex', justifyContent: 'space-between', fontWeight: 500 }}>
                  <span style={{ fontFamily: theme.font.display, fontSize: 18, color: theme.ink }}>{t('productDetail.totalToday')}</span>
                  <span style={{ fontFamily: theme.font.display, fontSize: 22, color: theme.ink }}>€{grandTotal.toFixed(2)}</span>
                </div>
                <div style={{ fontSize: 12, color: theme.grey400 }}>
                  {t('productDetail.rentalDepositNote', { rental: rentalTotal.toFixed(2), deposit: product.deposit_amount })}
                </div>
              </div>

              {/* What a late return costs, on the page where the price is
                  decided.
                  This block did not exist. The line above the day selector read
                  "Minimum 3 days · +€5/extra day" and nothing else on the page
                  mentioned money owed after the due date, so a reader took €5 to
                  be the cost of running over. Migration 032 split the two rates
                  and the penalty became €15 — three times what the page showed.
                  CRD Art. 6(1)(e) puts every charge the consumer may incur in
                  the pre-contractual information, and UCPD Art. 7 makes printing
                  a third of the real figure worse than printing nothing. */}
              <div style={{ borderTop: `1px solid ${theme.sand}`, marginTop: 20, paddingTop: 16, fontSize: 12, color: theme.grey500, lineHeight: 1.7 }}>
                <Trans
                  i18nKey="productDetail.lateFeeNotice"
                  values={{
                    lateFee: fmtRate(product.late_fee_per_day, 15),
                    extraDay: fmtRate(product.extra_day_price, 5),
                    deposit: product.deposit_amount,
                  }}
                  components={{ b: <strong style={{ color: theme.ink }} /> }}
                />
              </div>
            </div>

            <button
              onClick={handleRent}
              disabled={submitting || product.status !== 'available'}
              style={{
                width: '100%', padding: '18px', borderRadius: 2, border: 'none',
                background: product.status === 'available' ? theme.ink : theme.grey240,
                color: theme.white, fontSize: 13, letterSpacing: '0.15em',
                cursor: product.status === 'available' ? 'pointer' : 'not-allowed',
                fontFamily: theme.font.body, fontWeight: 500,
                marginBottom: 12
              }}
            >
              {submitting
                ? t('productDetail.creatingRental')
                : product.status === 'available'
                ? t('productDetail.rentNow', { price: rentalTotal.toFixed(2) })
                : t('productDetail.currentlyUnavailable')}
            </button>

            {product.status !== 'available' && (
              <button onClick={handleWaitlist} disabled={onWaitlist}
                style={{ width: '100%', padding: '14px', borderRadius: 2, border: `1px solid ${theme.gold}`, background: onWaitlist ? theme.creamDeep : 'transparent', color: theme.gold, fontSize: 12, letterSpacing: '0.12em', cursor: onWaitlist ? 'default' : 'pointer', fontFamily: theme.font.body, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 16 }}>
                <Bell size={14} /> {onWaitlist ? t('productDetail.notifying') : t('productDetail.notifyMe')}
              </button>
            )}
            {product.status === 'available' && <div style={{ marginBottom: 16 }} />}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, textAlign: 'center' }}>
              {[
                [<Shield size={16} />, t('productDetail.securePay')],
                [<Clock size={16} />, t('productDetail.locker247')],
                [<RotateCcw size={16} />, t('productDetail.depositBack')],
              ].map(([icon, label]) => (
                <div key={label} style={{ padding: 12, background: theme.white, border: `1px solid ${theme.sand}`, borderRadius: 4, color: theme.grey400, fontSize: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4, color: theme.gold }}>{icon}</div>
                  {label}
                </div>
              ))}
            </div>

            <div style={{ marginTop: 20, padding: 20, background: theme.creamDeep, borderRadius: 4, color: theme.goldOlive }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <MapPin size={16} color={theme.gold} />
                <strong style={{ fontSize: 13, color: theme.goldMoss }}>
                  {onePoint ? t('collectionPoint.titleOne') : t('collectionPoint.titleMany')}
                </strong>
              </div>
              <p style={{ fontSize: 12, lineHeight: 1.7, margin: '0 0 14px', color: theme.goldOlive }}>
                {onePoint ? t('collectionPoint.subtitleOne') : t('collectionPoint.subtitleMany')}
              </p>

              {machines === null ? (
                <div style={{ fontSize: 13 }}>{t('collectionPoint.loading')}</div>
              ) : machines.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {machines.map(m => (
                    <div key={m.id} style={{ background: theme.white, border: `1px solid ${theme.sand}`, borderRadius: 4, padding: 16 }}>
                      <div style={{ fontFamily: theme.font.display, fontSize: 18, color: theme.ink, marginBottom: 4 }}>{m.name}</div>
                      {m.address && <div style={{ fontSize: 13, color: theme.grey600, lineHeight: 1.6 }}>{m.address}</div>}
                      <div style={{ fontSize: 12, color: alwaysOpen(m) ? theme.green : theme.goldOlive, marginTop: 8 }}>
                        <strong>{t('collectionPoint.hoursLabel')}</strong>{' '}
                        {alwaysOpen(m) ? t('collectionPoint.alwaysOpen') : (m.opening_hours || t('collectionPoint.hoursUnknown'))}
                      </div>
                      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 10 }}>
                        {m.phone && (
                          <a href={`tel:${m.phone}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: theme.goldInk, fontSize: 12, textDecoration: 'none' }}>
                            <Phone size={13} /> {m.phone}
                          </a>
                        )}
                        {/* A link, not an embedded map: this URL hands the
                            coordinates to the native maps app inside the
                            Android wrapper and to the browser everywhere else,
                            without loading a map library onto a product page. */}
                        {mapsUrl(m) && (
                          <a href={mapsUrl(m)} target="_blank" rel="noopener noreferrer"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: theme.goldInk, fontSize: 12, textDecoration: 'none' }}>
                            <ExternalLink size={13} /> {t('collectionPoint.mapLink')}
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // The product's own text is a worse answer than a real address,
                // but it is a far better one than an empty box — so an outage or
                // an empty list falls back to it rather than to nothing.
                <div style={{ fontSize: 13 }}>
                  <strong>{t('productDetail.lockerLocation')}</strong> {product.locker_location || 'GlamGo Station, Nicosia Mall'}
                  {machinesFailed && (
                    <button onClick={() => setMachinesNonce(n => n + 1)}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 10, background: 'none', border: 'none', padding: 0, color: theme.goldInk, fontSize: 12, cursor: 'pointer', fontFamily: theme.font.body, textDecoration: 'underline' }}>
                      <RefreshCw size={12} /> {t('collectionPoint.retry')}
                    </button>
                  )}
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </div>

      {/* Product safety & traceability — GPSR (Reg. (EU) 2023/988) Art. 19.
          A rental is "making available on the market", so the article applies,
          and it applies to the OFFER: this page, not the Terms. The nickel and
          sensitive-scalp warning did exist before this block, buried in Terms §9
          — a different page from the one where somebody decides to wear the
          thing. Rendered above the reviews because it is about the product, and
          below the price because it is not what sells it. */}
      <SafetyBlock product={product} company={company} t={t} />

      {/* Reviews section */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px 80px' }}>
        <Reveal style={{ borderTop: `1px solid ${theme.sand}`, paddingTop: 56 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 32 }}>
            <h2 style={{ fontFamily: theme.font.display, fontSize: 32, fontWeight: 400, margin: 0 }}>{t('productDetail.reviews')}</h2>
            {reviews.average && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Stars rating={reviews.average} />
                <span style={{ fontFamily: theme.font.display, fontSize: 22 }}>{reviews.average}</span>
                <span style={{ color: theme.grey400, fontSize: 13 }}>({reviews.count})</span>
              </div>
            )}
          </div>

          {reviews.reviews.length === 0 ? (
            <p style={{ color: theme.grey400, fontSize: 14 }}>{t('productDetail.noReviews')}</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))', gap: 20 }}>
              {reviews.reviews.map(r => (
                <div key={r.id} style={{ background: theme.white, border: `1px solid ${theme.sand}`, borderRadius: 4, padding: 24 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <span style={{ fontWeight: 500, fontSize: 14 }}>{r.full_name || t('productDetail.customer')}</span>
                    <Stars rating={r.rating} />
                  </div>
                  {r.comment && <p style={{ color: theme.grey600, fontSize: 14, lineHeight: 1.6, margin: 0 }}>{r.comment}</p>}
                  <div style={{ color: theme.taupe, fontSize: 12, marginTop: 12 }}>
                    {new Date(r.created_at).toLocaleDateString(getIntlLocale())}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Art. 6a of the Consumer Rights Directive: state whether and how
              reviews are checked. Ours can only be written against a rental we
              have received back — but an undisclosed safeguard is not one. */}
          <p style={{ color: theme.grey400, fontSize: 12, lineHeight: 1.7, marginTop: 28 }}>
            {t('landing.reviewsVerified')}
          </p>
        </Reveal>
      </div>

      <style>{`@media (max-width: 768px) { .product-grid { grid-template-columns: 1fr !important; } }`}</style>
    </div>
  );
}

/**
 * The Art. 19 block.
 *
 * Renders nothing when the product carries none of it. That is not a loophole —
 * routes/products.js refuses to set `is_active` on a product missing any of
 * these fields, so a published product always has them and this can only be
 * empty for a draft an operator is previewing. Better a preview with a gap in it
 * than a live page with eight empty labels.
 */
function SafetyBlock({ product, company, t }) {
  const warning = product.safety_warning;
  const rows = [
    ['manufacturer', product.manufacturer_name],
    ['manufacturerAddress', product.manufacturer_address],
    ['manufacturerEmail', product.manufacturer_email],
    ['model', product.model],
    ['batch', product.batch_code],
    ['material', product.material_composition],
    ['origin', product.country_of_origin],
  ].filter(([, v]) => v);

  if (!rows.length && !warning) return null;

  // Art. 16: where the manufacturer is not established in the Union, the offer
  // must also name the responsible person inside it — which is us, as importer.
  // Read from GET /api/company rather than duplicated onto every product, so
  // filling COMPANY_* once fills it everywhere.
  const needsResponsiblePerson = product.manufacturer_in_eu === false && company.configured;

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
      <Reveal style={{ borderTop: `1px solid ${theme.sand}`, paddingTop: 48, paddingBottom: 8 }}>
        <h2 style={{ fontFamily: theme.font.display, fontSize: 26, fontWeight: 400, margin: '0 0 8px' }}>
          {t('productDetail.safetyTitle')}
        </h2>
        <p style={{ color: theme.grey400, fontSize: 13, margin: '0 0 28px' }}>{t('productDetail.safetyIntro')}</p>

        {/* The warning first and loudest. It is the only part of this block a
            customer needs before wearing them; the rest is traceability. */}
        {warning && (
          <div style={{ background: theme.alpha.gold(0.09), border: `1px solid ${theme.alpha.gold(0.3)}`, borderRadius: 4, padding: '18px 22px', marginBottom: 28 }}>
            <div style={{ fontSize: 11, letterSpacing: '0.14em', color: theme.goldInkAlt, marginBottom: 8, textTransform: 'uppercase' }}>
              {t('productDetail.safetyWarningLabel')}
            </div>
            <p style={{ color: theme.ink700, fontSize: 14, lineHeight: 1.75, margin: 0, whiteSpace: 'pre-wrap' }}>{warning}</p>
          </div>
        )}

        <dl style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))', gap: '18px 40px', margin: 0 }}>
          {rows.map(([key, value]) => (
            <div key={key}>
              <dt style={{ fontSize: 11, letterSpacing: '0.12em', color: theme.grey400, textTransform: 'uppercase', marginBottom: 4 }}>
                {t(`productDetail.${key}`)}
              </dt>
              <dd style={{ margin: 0, fontSize: 14, color: theme.grey600, lineHeight: 1.6, wordBreak: 'break-word' }}>{value}</dd>
            </div>
          ))}
        </dl>

        {needsResponsiblePerson && (
          <div style={{ marginTop: 28, paddingTop: 20, borderTop: `1px solid ${theme.sand}` }}>
            <div style={{ fontSize: 11, letterSpacing: '0.12em', color: theme.grey400, textTransform: 'uppercase', marginBottom: 6 }}>
              {t('productDetail.responsiblePerson')}
            </div>
            <p style={{ color: theme.grey500, fontSize: 12.5, lineHeight: 1.6, margin: '0 0 8px' }}>{t('productDetail.responsiblePersonNote')}</p>
            <TraderIdentity style={{ color: theme.grey600, fontSize: 13, lineHeight: 1.8, margin: 0 }} />
          </div>
        )}
      </Reveal>
    </div>
  );
}

function Stars({ rating }) {
  return (
    <div style={{ display: 'flex', gap: 2 }}>
      {[1,2,3,4,5].map(i => (
        <Star key={i} size={14} fill={i <= Math.round(rating) ? theme.gold : 'none'} color={theme.gold} />
      ))}
    </div>
  );
}
